import { type Observable } from 'rxjs'
import { type SnackbarErrorHandlerFactory } from '@/features/shell'
import { createInternalFeatureLoader } from '@/utils/feature-loading-util'
import {
  type PersonalProfileEditorViewModel,
  type PersonalProfileEditorViewModelOptions,
} from '@/features/tax-profile/individual-profiles/PersonalProfileEditorViewModel'
import {
  type SpousalProfileEditorViewModel,
  type SpousalProfileEditorViewModelOptions,
} from '@/features/tax-profile/individual-profiles/SpousalProfileEditorViewModel'
import {
  type DependentProfileEditorViewModel,
  type DependentProfileEditorViewModelOptions,
} from '@/features/tax-profile/individual-profiles/DependentProfileEditorViewModel'
import type { GetCurrentMemberFn } from '@/features/iam'
import type { Id } from '@/infra/id'
import type { PersonalProfileVersion } from '@/features/tax-profile/individual-profiles/PersonalProfile'
import type { SpousalProfileVersion } from '@/features/tax-profile/individual-profiles/SpousalProfile'
import type { DependentProfileVersion } from '@/features/tax-profile/individual-profiles/DependentProfile'
import type { CursorPaged } from '@/infra/paging'
import type { ListDependentsQuery } from '@/features/tax-profile/individual-profiles/IndividualProfileStore'
import {
  type TaxRefundMethodEditorViewModel,
  type TaxRefundMethodEditorViewModelOptions,
} from '@/features/tax-profile/tax-refund-methods/TaxRefundMethodEditorViewModel'
import type { TaxRefundMethodVersion } from '@/features/tax-profile/tax-refund-methods/TaxRefundMethod'
import type { ListTaxRefundMethodsQuery } from '@/features/tax-profile/tax-refund-methods/TaxRefundMethodStore'
import {
  type BusinessProfileEditorViewModel,
  type BusinessProfileEditorViewModelOptions,
} from '@/features/tax-profile/business-profiles/BusinessProfileEditorViewModel'
import type { BusinessProfileVersion } from '@/features/tax-profile/business-profiles/BusinessProfile'
import type { ListBusinessProfilesQuery } from '@/features/tax-profile/business-profiles/BusinessProfileStore'

/**
 * Options for the {@link TaxProfileFeature}.
 */
export interface TaxProfileFeatureOptions {
  token$: Observable<string>
  createSnackbarErrorHandler: SnackbarErrorHandlerFactory
  getCurrentMember: GetCurrentMemberFn
}

/**
 * Factory for creating a {@link PersonalProfileEditorViewModel}.
 */
export type PersonalProfileEditorViewModelFactory = (
  options: PersonalProfileEditorViewModelOptions,
) => PersonalProfileEditorViewModel

/**
 * Factory for creating a {@link SpousalProfileEditorViewModel}.
 */
export type SpousalProfileEditorViewModelFactory = (
  options: SpousalProfileEditorViewModelOptions,
) => SpousalProfileEditorViewModel

/**
 * Factory for creating a {@link DependentProfileEditorViewModel}.
 */
export type DependentProfileEditorViewModelFactory = (
  options: DependentProfileEditorViewModelOptions,
) => DependentProfileEditorViewModel

/**
 * Factory for creating a {@link TaxRefundMethodEditorViewModel}.
 */
export type TaxRefundMethodEditorViewModelFactory = (
  options: TaxRefundMethodEditorViewModelOptions,
) => TaxRefundMethodEditorViewModel

/**
 * Factory for creating a {@link BusinessProfileEditorViewModel}.
 */
export type BusinessProfileEditorViewModelFactory = (
  options: BusinessProfileEditorViewModelOptions,
) => BusinessProfileEditorViewModel

/**
 * Delegate for fetching a personal profile that has been linked to a job.
 */
export type FetchPersonalProfileVersionForJobFn = (
  jobId: Id,
  signal?: AbortSignal,
) => Promise<PersonalProfileVersion | null>

/**
 * Delegate for fetching a spousal profile that has been linked to a job.
 */
export type FetchSpousalProfileVersionForJobFn = (
  jobId: Id,
  signal?: AbortSignal,
) => Promise<SpousalProfileVersion | null>

/**
 * Delegate for fetching a dependent profile that has been linked to a job.
 */
export type FetchDependentProfileVersionForJobFn = (
  jobId: Id,
  profileId: Id,
  signal?: AbortSignal,
) => Promise<DependentProfileVersion | null>

/**
 * Delegate for listing dependents in a workspace.
 */
export type ListDependentsFn = (
  query: ListDependentsQuery,
  signal?: AbortSignal,
) => Promise<CursorPaged<DependentProfileVersion>>

/**
 * Prompts for and performs deletion if accepted.
 *
 * @param dependent
 * @param signal
 * @returns A boolean promise that resolves to true if the
 *  dependent was deleted, or false if the operation was cancelled or
 *  the user declined.
 */
export type PromptDeleteDependentFn = (
  dependent: DependentProfileVersion,
  signal?: AbortSignal,
) => Promise<boolean>

/**
 * Delegate for fetching a business profile that has been linked to a job.
 */
export type FetchBusinessProfileForJobFn = (
  jobId: Id,
  signal?: AbortSignal,
) => Promise<BusinessProfileVersion | null>

/**
 * Delegate for listing business profiles.
 */
export type ListBusinessProfilesFn = (
  query: ListBusinessProfilesQuery,
  signal?: AbortSignal,
) => Promise<CursorPaged<BusinessProfileVersion>>

/**
 * Delegate for fetching a personal tax refund method that has been linked to a job.
 */
export type FetchPersonalTaxRefundMethodForJobFn = (
  jobId: Id,
  signal?: AbortSignal,
) => Promise<TaxRefundMethodVersion | null>

/**
 * Delegate for fetching a business tax refund method that has been linked to a job.
 */
export type FetchBusinessTaxRefundMethodForJobFn = (
  jobId: Id,
  signal?: AbortSignal,
) => Promise<TaxRefundMethodVersion | null>

/**
 * Delegate for listing tax refund methods.
 */
export type ListTaxRefundMethodsFn = (
  query: ListTaxRefundMethodsQuery,
  signal?: AbortSignal,
) => Promise<CursorPaged<TaxRefundMethodVersion>>

/**
 * Prompts for and performs deletion if accepted.
 *
 * @param method
 * @param signal
 * @returns A boolean promise that resolves to true if the
 *  method was deleted, or false if the operation was cancelled or
 *  the user declined.
 */
export type PromptDeleteTaxRefundMethodFn = (
  method: TaxRefundMethodVersion,
  signal?: AbortSignal,
) => Promise<boolean>

/**
 * Prompts for and performs deletion if accepted.
 *
 * @param profile
 * @param signal
 * @returns A boolean promise that resolves to true if the
 *  profile was deleted, or false if the operation was cancelled or
 *  the user declined.
 */
export type PromptDeleteBusinessProfileFn = (
  profile: BusinessProfileVersion,
  signal?: AbortSignal,
) => Promise<boolean>

/**
 * Everything related to managing the current user's profile.
 */
export type TaxProfileFeature = ReturnType<typeof TaxProfileFeature>

export function TaxProfileFeature(options: TaxProfileFeatureOptions) {
  const internal = createInternalFeatureLoader(
    () => import('./internal'),
    (module) => module.InternalTaxProfileFeature(options),
  )

  const promptDeleteDependent: PromptDeleteDependentFn = internal.wrap(
    (m) => m.promptDeleteDependent,
  )

  return {
    // Internal
    internal,

    // Public
    createPersonalProfileEditorViewModel: internal.lazy(
      (m) => m.createPersonalProfileEditorViewModel,
    ),
    createSpousalProfileEditorViewModel: internal.lazy(
      (m) => m.createSpousalProfileEditorViewModel,
    ),
    createDependentProfileEditorViewModel: internal.lazy(
      (m) => m.createDependentProfileEditorViewModel,
    ),
    createTaxRefundMethodEditorViewModel: internal.lazy(
      (m) => m.createTaxRefundMethodEditorViewModel,
    ),
    createBusinessProfileEditorViewModel: internal.lazy(
      (m) => m.createBusinessProfileEditorViewModel,
    ),
    fetchPersonalProfileVersionForJob: internal.wrap(
      (m) => m.fetchPersonalProfileVersionForJob,
    ),
    fetchSpousalProfileVersionForJob: internal.wrap(
      (m) => m.fetchSpousalProfileVersionForJob,
    ),
    fetchDependentProfileVersionForJob: internal.wrap(
      (m) => m.fetchDependentProfileVersionForJob,
    ),
    fetchBusinessProfileForJob: internal.wrap(
      (m) => m.fetchBusinessProfileForJob,
    ),
    listBusinessProfiles: internal.wrap((m) => m.listBusinessProfiles),
    listDependents: internal.wrap((m) => m.listDependents),
    promptDeleteDependent,
    fetchPersonalTaxRefundMethodForJob: internal.wrap(
      (m) => m.fetchPersonalTaxRefundMethodForJob,
    ),
    fetchBusinessTaxRefundMethodForJob: internal.wrap(
      (m) => m.fetchBusinessTaxRefundMethodForJob,
    ),
    listTaxRefundMethods: internal.wrap((m) => m.listTaxRefundMethods),
    promptDeleteTaxRefundMethod: internal.wrap(
      (m) => m.promptDeleteTaxRefundMethod,
    ),
    promptDeleteBusinessProfile: internal.wrap(
      (m) => m.promptDeleteBusinessProfile,
    ),
  }
}
